import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, Plus, Minus, Check, Package, Barcode, Building2, Tag, Ruler, Loader2, ArrowUpRight } from "lucide-react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { useCart } from "../context/CartContext";
import { ComplementStrip } from "./ComplementStrip";
import { formatCOP, clientApi } from "../lib/api";
import { formatUnitFull } from "../lib/units";
import { toast } from "sonner";

// Reusa la paleta del ProductCard cuando no hay imagen
const PALETTE = [
  { bg: "#FEF3C7", fg: "#92400E" }, { bg: "#DBEAFE", fg: "#1E40AF" },
  { bg: "#FCE7F3", fg: "#9F1239" }, { bg: "#D1FAE5", fg: "#065F46" },
  { bg: "#E0E7FF", fg: "#3730A3" }, { bg: "#FED7AA", fg: "#9A3412" },
  { bg: "#F3E8FF", fg: "#6B21A8" }, { bg: "#FEE2E2", fg: "#991B1B" },
  { bg: "#CCFBF1", fg: "#115E59" },
];
const hashStr = (s) => { let h=0; for (let i=0;i<(s||"").length;i++) h=(h*31+s.charCodeAt(i))|0; return Math.abs(h); };

/**
 * Modal de ficha completa del producto.
 *
 * Se abre desde el ProductCard al hacer click en la imagen o en el nombre.
 * Fetch a /api/portal/products/{codigo}/detail para descripción larga, código
 * de barras, marca, proveedor, etc. Permite ajustar cantidad y agregar al carrito.
 */
export const ProductDetailModal = ({ codigo, open, onClose, initial }) => {
  // Seed the ficha with what the catalog already knows so the modal paints
  // instantly on mobile; the server detail (descripción larga, cód. barras)
  // is merged in when it arrives.
  const seed = initial
    ? {
        codigo: initial.codigo,
        nombre: initial.descripcion,
        categoria: initial.categoria,
        unidad: initial.unidad,
        marca: initial.marca,
        proveedor: initial.proveedor,
        imagen: initial.imagen,
        precio: initial.precio,
        precio_sin_iva: initial.precio_sin_iva,
        iva_porcentaje: initial.iva_porcentaje,
        inventario: initial.inventario,
        disponible: initial.disponible,
        tiene_psm: initial.tiene_psm,
        sublinea: initial.sublinea,
        sub_sublinea: initial.sub_sublinea,
        linea: initial.linea,
        cod_bar: initial.cod_bar,
      }
    : null;
  const [detail, setDetail] = useState(seed);
  const [loading, setLoading] = useState(false);
  const [qty, setQty] = useState(1);
  const [added, setAdded] = useState(false);
  const { add } = useCart();
  const navigate = useNavigate();

  const goToMarca = (marca) => {
    if (!marca) return;
    onClose();
    navigate(`/catalogo?marca=${encodeURIComponent(marca)}`);
  };
  const goToProveedor = (proveedor) => {
    if (!proveedor) return;
    onClose();
    navigate(`/catalogo?proveedor=${encodeURIComponent(proveedor)}`);
  };

  useEffect(() => {
    if (!open || !codigo) return;
    let cancelled = false;
    setLoading(true);
    setQty(1);
    clientApi.get(`/portal/products/${encodeURIComponent(codigo)}/detail`)
      .then((r) => { if (!cancelled) setDetail((prev) => ({ ...(prev || {}), ...r.data })); })
      .catch(() => { if (!cancelled) toast.error("No se pudo cargar la ficha del producto"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, codigo]);

  // Close on ESC
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  const onAdd = () => {
    if (!detail?.disponible) return;
    add({
      codigo: detail.codigo,
      descripcion: detail.nombre,
      precio: detail.precio,
      precio_sin_iva: detail.precio_sin_iva,
      unidad: detail.unidad,
      imagen: detail.imagen,
      iva_porcentaje: detail.iva_porcentaje,
      iva_incluido: true,
    }, qty);
    setAdded(true);
    toast.success(`${(detail.nombre || "").substring(0, 40)} agregado`, { duration: 1500 });
    setTimeout(() => { setAdded(false); onClose(); }, 800);
  };

  if (!open) return null;

  const p = detail ? PALETTE[hashStr(detail.categoria || "") % PALETTE.length] : PALETTE[0];

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4 sm:p-6" data-testid="product-detail-modal">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-cm-ink/70 backdrop-blur-sm" onClick={onClose} />
      {/* Modal */}
      <div
        className="relative z-10 bg-cm-cream w-full max-w-4xl max-h-[92vh] rounded-md overflow-hidden shadow-2xl border border-cm-border grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 z-20 h-9 w-9 rounded-full bg-white/80 backdrop-blur hover:bg-white text-cm-ink flex items-center justify-center transition-colors"
          aria-label="Cerrar ficha"
          data-testid="close-detail-modal"
        >
          <X className="h-4 w-4" />
        </button>

        {/* IMAGE side */}
        <div className="relative bg-white aspect-square md:aspect-auto md:min-h-[520px] flex items-center justify-center p-4 md:p-6">
          {!detail ? (
            <div className="h-full w-full flex items-center justify-center">
              <Loader2 className="h-8 w-8 text-cm-ink-soft animate-spin" />
            </div>
          ) : detail.imagen ? (
            <img
              src={detail.imagen}
              alt={detail.nombre}
              className="max-h-full max-w-full object-contain"
              data-testid="detail-image"
            />
          ) : (
            <div
              className="h-full w-full flex flex-col items-center justify-center rounded-md"
              style={{ background: `linear-gradient(135deg, ${p.bg} 0%, ${p.bg}88 100%)` }}
            >
              <span className="font-serif text-8xl font-medium tracking-tight" style={{ color: p.fg }}>
                {(detail.categoria || "·").trim().slice(0, 2).toUpperCase()}
              </span>
              <span className="text-xs mt-3 font-mono uppercase tracking-widest opacity-60" style={{ color: p.fg }}>
                {detail.categoria || "Producto"}
              </span>
            </div>
          )}
          {detail?.tiene_psm && (
            <Badge className="absolute top-3 left-3 bg-cm-yellow text-cm-ink hover:bg-cm-yellow text-xs px-2 py-1 font-bold uppercase tracking-widest">
              Precio especial
            </Badge>
          )}
        </div>

        {/* INFO side */}
        <div className="p-6 sm:p-8 flex flex-col overflow-y-auto max-h-[92vh]">
          {!detail ? (
            <div className="flex-1 flex items-center justify-center text-cm-ink-soft text-sm">Cargando ficha…</div>
          ) : (
            <>
              <div className="flex items-center gap-2 flex-wrap">
                <Badge className="bg-cm-ink text-cm-cream hover:bg-cm-ink text-[10px] font-mono">#{detail.codigo}</Badge>
                {(detail.linea || detail.categoria) && (
                  <Badge variant="outline" className="border-cm-border text-cm-ink-soft text-[10px] uppercase tracking-wider">
                    {detail.linea || detail.categoria}
                  </Badge>
                )}
              </div>
              {(detail.sublinea || detail.sub_sublinea) && (
                <p className="mt-2 text-[11px] text-cm-ink-soft uppercase tracking-wider" data-testid="detail-jerarquia">
                  {[detail.linea || detail.categoria, detail.sublinea, detail.sub_sublinea]
                    .filter(Boolean)
                    .join(" › ")}
                </p>
              )}
              <h2 className="font-serif text-2xl sm:text-3xl mt-3 leading-tight text-cm-ink" data-testid="detail-nombre">
                {detail.nombre}
              </h2>

              {/* Marca + proveedor destacados */}
              <div className="mt-4 flex flex-wrap gap-3">
                {detail.marca && (
                  <button
                    type="button"
                    onClick={() => goToMarca(detail.marca)}
                    className="group flex items-center gap-2 px-3 py-2 bg-white border border-cm-border rounded-md hover:border-cm-red hover:bg-cm-red/5 transition-colors text-left"
                    data-testid="ver-mas-marca-btn"
                    title={`Ver más productos de ${detail.marca}`}
                  >
                    <Tag className="h-4 w-4 text-cm-red" />
                    <div className="flex-1">
                      <div className="text-[10px] uppercase tracking-widest text-cm-ink-soft">Marca</div>
                      <div className="text-sm font-medium text-cm-ink group-hover:text-cm-red flex items-center gap-1" data-testid="detail-marca">
                        {detail.marca}
                        <ArrowUpRight className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                    </div>
                  </button>
                )}
                {detail.proveedor && (
                  <button
                    type="button"
                    onClick={() => goToProveedor(detail.proveedor)}
                    className="group flex items-center gap-2 px-3 py-2 bg-white border border-cm-border rounded-md hover:border-cm-ink hover:bg-cm-cream-alt transition-colors text-left"
                    data-testid="ver-mas-proveedor-btn"
                    title={`Ver más productos de ${detail.proveedor}`}
                  >
                    <Building2 className="h-4 w-4 text-cm-ink-soft" />
                    <div className="flex-1">
                      <div className="text-[10px] uppercase tracking-widest text-cm-ink-soft">Proveedor</div>
                      <div className="text-sm text-cm-ink group-hover:text-cm-ink flex items-center gap-1" data-testid="detail-proveedor">
                        <span className="truncate max-w-[200px]">{detail.proveedor}</span>
                        <ArrowUpRight className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                    </div>
                  </button>
                )}
              </div>

              {/* Descripción larga (solo si Arcos la trae) */}
              {loading && !detail.descripcion_larga && (
                <p className="mt-4 text-xs text-cm-ink-soft inline-flex items-center gap-2" data-testid="detail-loading">
                  <Loader2 className="h-3 w-3 animate-spin" /> Cargando ficha completa…
                </p>
              )}
              {detail.descripcion_larga && (
                <div className="mt-4">
                  <div className="text-[10px] uppercase tracking-widest text-cm-ink-soft font-medium mb-1">Descripción</div>
                  <p className="text-sm text-cm-ink leading-relaxed whitespace-pre-line" data-testid="detail-descripcion">
                    {detail.descripcion_larga}
                  </p>
                </div>
              )}

              {/* Datos técnicos */}
              <div className="mt-5 grid grid-cols-2 gap-3 text-xs">
                {detail.unidad && (
                  <div className="flex items-start gap-2">
                    <Ruler className="h-3.5 w-3.5 text-cm-ink-soft mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-cm-ink-soft">Unidad</div>
                      <div className="text-cm-ink" data-testid="detail-unidad">{formatUnitFull(detail.unidad)}</div>
                    </div>
                  </div>
                )}
                {detail.cod_bar && (
                  <div className="flex items-start gap-2">
                    <Barcode className="h-3.5 w-3.5 text-cm-ink-soft mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-cm-ink-soft">Cód. barras</div>
                      <div className="text-cm-ink font-mono text-[11px]">{detail.cod_bar}</div>
                    </div>
                  </div>
                )}
                {typeof detail.inventario === "number" && (
                  <div className="flex items-start gap-2">
                    <Package className="h-3.5 w-3.5 text-cm-ink-soft mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-cm-ink-soft">Disponibilidad</div>
                      <div className={detail.inventario > 50 ? "text-green-700" : detail.inventario > 0 ? "text-cm-yellow font-medium" : "text-cm-red font-medium"}>
                        {detail.inventario > 50 ? "En stock" : detail.inventario > 0 ? `Solo ${detail.inventario} unid.` : "Agotado"}
                      </div>
                    </div>
                  </div>
                )}
                {detail.lista_precios_nombre && (
                  <div className="flex items-start gap-2">
                    <Tag className="h-3.5 w-3.5 text-cm-ink-soft mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-cm-ink-soft">Tu lista</div>
                      <div className="text-cm-ink">{detail.lista_precios_nombre}</div>
                    </div>
                  </div>
                )}
              </div>

              <div className="my-6 h-px bg-cm-border" />

              {/* Precio + agregar */}
              {detail.disponible ? (
                <>
                  <div className="flex items-baseline gap-2">
                    <span className="text-[10px] uppercase tracking-widest text-cm-ink-soft">Precio</span>
                    <span className="text-3xl font-bold font-mono text-cm-ink" data-testid="detail-precio">
                      {formatCOP(detail.precio)}
                    </span>
                    {detail.iva_porcentaje > 0 && (
                      <span className="text-[10px] text-cm-ink-soft">IVA {detail.iva_porcentaje}% incluido</span>
                    )}
                  </div>

                  <div className="mt-5 flex items-center gap-3">
                    <div className="flex items-center border border-cm-border rounded bg-white">
                      <button
                        onClick={() => setQty(Math.max(1, qty - 1))}
                        className="h-11 w-11 hover:bg-cm-cream-alt flex items-center justify-center"
                        data-testid="detail-qty-decrease"
                      ><Minus className="h-4 w-4" /></button>
                      <input
                        type="number"
                        min={1}
                        value={qty}
                        onChange={(e) => setQty(Math.max(1, parseInt(e.target.value) || 1))}
                        className="w-16 h-11 text-center text-base font-mono bg-transparent focus:outline-none"
                        data-testid="detail-qty-input"
                      />
                      <button
                        onClick={() => setQty(qty + 1)}
                        className="h-11 w-11 hover:bg-cm-cream-alt flex items-center justify-center"
                        data-testid="detail-qty-increase"
                      ><Plus className="h-4 w-4" /></button>
                    </div>
                    <Button
                      onClick={onAdd}
                      size="lg"
                      className={`flex-1 h-11 transition-all ${added ? "bg-green-600 hover:bg-green-600" : "btn-cm-primary"}`}
                      data-testid="detail-add-to-cart"
                    >
                      {added ? <><Check className="h-4 w-4 mr-2" /> Agregado</> : `Agregar ${qty > 1 ? `${qty} ` : ""}al carrito`}
                    </Button>
                  </div>
                </>
              ) : (
                <div className="p-4 bg-cm-red/5 border border-cm-red/30 rounded-md text-sm text-cm-red">
                  Este producto no tiene precio activo en tu lista comercial. Consulta con tu asesor CM.
                </div>
              )}

              <ComplementStrip codigo={detail.codigo} />
            </>
          )}
        </div>
      </div>
    </div>
  );
};
